/**
 * Para hesapları. Tüm tutarlar para biriminin en küçük biriminde (kuruş, cent) TAM SAYI olarak tutulur.
 * Ondalıklı sayı (float) ile para hesabı yapılmaz. Oranlar "baz puan" (bps, 1% = 100 bps) ile ifade edilir.
 */

export interface Money {
  amountMinor: number;
  currency: string;
}

export type RoundingMode = 'half_up' | 'half_even' | 'down' | 'up';

const MINOR_UNITS: Record<string, number> = {
  TRY: 2,
  EUR: 2,
  USD: 2,
  GBP: 2,
  AED: 2,
  SAR: 2,
  AZN: 2,
  JPY: 0,
  KWD: 3,
};

export class MoneyError extends Error {}

export function minorUnitOf(currency: string): number {
  const u = MINOR_UNITS[currency];
  if (u === undefined) throw new MoneyError(`Desteklenmeyen para birimi: ${currency}`);
  return u;
}

function assertSafe(n: number, label = 'tutar'): void {
  if (!Number.isSafeInteger(n)) throw new MoneyError(`${label} güvenli bir tam sayı değil: ${n}`);
}

export function money(amountMinor: number, currency: string): Money {
  assertSafe(amountMinor);
  minorUnitOf(currency);
  return { amountMinor, currency };
}

function sameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) throw new MoneyError(`Para birimleri farklı: ${a.currency} / ${b.currency}`);
}

export function add(a: Money, b: Money): Money {
  sameCurrency(a, b);
  return money(a.amountMinor + b.amountMinor, a.currency);
}

export function subtract(a: Money, b: Money): Money {
  sameCurrency(a, b);
  return money(a.amountMinor - b.amountMinor, a.currency);
}

export function sum(items: Money[], currency: string): Money {
  return items.reduce((acc, m) => add(acc, m), money(0, currency));
}

export function multiplyByInt(a: Money, quantity: number): Money {
  assertSafe(quantity, 'adet');
  return money(a.amountMinor * quantity, a.currency);
}

export function compare(a: Money, b: Money): -1 | 0 | 1 {
  sameCurrency(a, b);
  return a.amountMinor === b.amountMinor ? 0 : a.amountMinor < b.amountMinor ? -1 : 1;
}

export function isZero(a: Money): boolean {
  return a.amountMinor === 0;
}

/** Tam sayı bölmesini istenen kurala göre yuvarlar (BigInt ile; taşma ve kayan nokta hatası yok). */
export function divRound(numerator: bigint, denominator: bigint, mode: RoundingMode = 'half_up'): bigint {
  if (denominator === 0n) throw new MoneyError('Sıfıra bölme');
  if (denominator < 0n) {
    numerator = -numerator;
    denominator = -denominator;
  }
  const negative = numerator < 0n;
  const abs = negative ? -numerator : numerator;
  const q = abs / denominator;
  const r = abs % denominator;
  let result = q;
  if (r !== 0n) {
    switch (mode) {
      case 'down':
        break;
      case 'up':
        result = q + 1n;
        break;
      case 'half_up':
        if (r * 2n >= denominator) result = q + 1n;
        break;
      case 'half_even': {
        const twice = r * 2n;
        if (twice > denominator || (twice === denominator && q % 2n === 1n)) result = q + 1n;
        break;
      }
    }
  }
  return negative ? -result : result;
}

/** "20", "20.5", "%18", "0,5" → baz puan (2000, 2050, 1800, 50). En fazla 2 ondalık. */
export function percentToBps(input: string | number): number {
  const s = String(input).trim().replace('%', '').replace(',', '.');
  const m = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(s);
  if (!m) throw new MoneyError(`Geçersiz yüzde: ${input}`);
  const sign = m[1] === '-' ? -1 : 1;
  const whole = Number(m[2]);
  const frac = Number((m[3] ?? '').padEnd(2, '0'));
  return sign * (whole * 100 + frac);
}

/** Tutarın belirli bir yüzdesi (bps). */
export function percentOf(a: Money, bps: number, mode: RoundingMode = 'half_up'): Money {
  assertSafe(bps, 'oran');
  const v = divRound(BigInt(a.amountMinor) * BigInt(bps), 10_000n, mode);
  return money(Number(v), a.currency);
}

/** Tutarı yüzde kadar artırır (negatif bps azaltır). Toplu fiyat güncelleme bunu kullanır. */
export function adjustByPercent(a: Money, bps: number, mode: RoundingMode = 'half_up'): Money {
  return add(a, percentOf(a, bps, mode));
}

export interface TaxBreakdown {
  net: Money;
  tax: Money;
  gross: Money;
}

/** KDV dahil tutardan KDV'yi ayırır. gross = net + tax her zaman tam olarak sağlanır. */
export function taxFromGross(gross: Money, rateBps: number, mode: RoundingMode = 'half_up'): TaxBreakdown {
  const net = divRound(BigInt(gross.amountMinor) * 10_000n, BigInt(10_000 + rateBps), mode);
  const netM = money(Number(net), gross.currency);
  return { net: netM, tax: subtract(gross, netM), gross };
}

/** KDV hariç tutara KDV ekler. */
export function taxFromNet(net: Money, rateBps: number, mode: RoundingMode = 'half_up'): TaxBreakdown {
  const tax = percentOf(net, rateBps, mode);
  return { net, tax, gross: add(net, tax) };
}

/**
 * Tutarı oranlara göre böler; kuruş kaybı olmaz (toplam her zaman korunur).
 * Ör. kupon indiriminin sepet satırlarına dağıtılması.
 */
export function allocate(total: Money, weights: number[]): Money[] {
  if (weights.length === 0) throw new MoneyError('Ağırlık listesi boş');
  weights.forEach((w) => {
    if (!Number.isSafeInteger(w) || w < 0) throw new MoneyError('Ağırlıklar negatif olmayan tam sayı olmalı');
  });
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  if (totalWeight === 0) throw new MoneyError('Ağırlık toplamı sıfır');
  const t = BigInt(total.amountMinor);
  const shares = weights.map((w) => (t * BigInt(w)) / BigInt(totalWeight));
  let remainder = t - shares.reduce((a, b) => a + b, 0n);
  const step = remainder >= 0n ? 1n : -1n;
  for (let i = 0; remainder !== 0n; i = (i + 1) % shares.length) {
    if (weights[i] === 0) continue;
    shares[i] = (shares[i] ?? 0n) + step;
    remainder -= step;
  }
  return shares.map((s) => money(Number(s), total.currency));
}

/** Kullanıcı girdisini ("1.299,90", "1299.9", "1 299,90 ₺") kayan nokta kullanmadan kuruşa çevirir. */
export function parseAmount(input: string, currency: string): Money {
  const unit = minorUnitOf(currency);
  const s = input.replace(/[^\d.,-]/g, '');
  if (!s || s === '-') throw new MoneyError(`Geçersiz tutar: ${input}`);
  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  let decimalSep: string | null = null;
  if (lastComma > -1 && lastDot > -1) decimalSep = lastComma > lastDot ? ',' : '.';
  else if (lastComma > -1) decimalSep = s.length - lastComma - 1 <= unit ? ',' : null;
  else if (lastDot > -1)
    decimalSep = s.length - lastDot - 1 <= unit && s.split('.').length === 2 ? '.' : null;
  let intPart = s;
  let fracPart = '';
  if (decimalSep) {
    const idx = s.lastIndexOf(decimalSep);
    intPart = s.slice(0, idx);
    fracPart = s.slice(idx + 1);
  }
  intPart = intPart.replace(/[.,]/g, '');
  const negative = intPart.startsWith('-');
  intPart = intPart.replace('-', '');
  if (!/^\d*$/.test(intPart) || !/^\d*$/.test(fracPart) || fracPart.length > unit) {
    throw new MoneyError(`Geçersiz tutar: ${input}`);
  }
  const minor =
    BigInt(intPart || '0') * 10n ** BigInt(unit) + BigInt((fracPart || '').padEnd(unit, '0') || '0');
  return money(Number(negative ? -minor : minor), currency);
}

/** Görüntüleme için biçimlendirir (ör. "1.299,90 ₺"). Hesap için kullanılmaz. */
/**
 * Intl.NumberFormat nesnesi oluşturmak pahalıdır (F11-05: vitrin sunucusunun işlemci profilinde uygulama kodunun en
 * çok zaman harcadığı yer buydu — her fiyat için üç yeni nesne). Dil ve para birimi başına bir kez oluşturulur.
 */
const formatters = new Map<string, Intl.NumberFormat>();
const decimalSymbols = new Map<string, string>();
function numberFormat(key: string, locale: string, opts?: Intl.NumberFormatOptions): Intl.NumberFormat {
  let f = formatters.get(key);
  if (!f) {
    if (formatters.size > 500) formatters.clear();
    f = new Intl.NumberFormat(locale, opts);
    formatters.set(key, f);
  }
  return f;
}
function decimalSymbolOf(locale: string): string {
  let s = decimalSymbols.get(locale);
  if (s === undefined) {
    if (decimalSymbols.size > 200) decimalSymbols.clear();
    s = new Intl.NumberFormat(locale).formatToParts(1.5).find((p) => p.type === 'decimal')?.value ?? ',';
    decimalSymbols.set(locale, s);
  }
  return s;
}

export function format(a: Money, locale = 'tr-TR'): string {
  const unit = minorUnitOf(a.currency);
  const negative = a.amountMinor < 0;
  const abs = BigInt(Math.abs(a.amountMinor));
  const base = 10n ** BigInt(unit);
  const intStr = (abs / base).toString();
  const fracStr = unit > 0 ? (abs % base).toString().padStart(unit, '0') : '';
  // Tam sayı kısmını Intl ile gruplandır; kesir kısmını string olarak ekle (kayan nokta yok).
  const parts = numberFormat(`c|${locale}|${a.currency}`, locale, {
    style: 'currency',
    currency: a.currency,
  }).formatToParts(negative ? -1 : 1);
  const grouped = numberFormat(`g|${locale}`, locale, { useGrouping: true, maximumFractionDigits: 0 }).format(
    BigInt(intStr),
  );
  const decimalSymbol = decimalSymbolOf(locale);
  const numberStr = unit > 0 ? `${grouped}${decimalSymbol}${fracStr}` : grouped;
  let out = '';
  let replaced = false;
  for (const p of parts) {
    if (p.type === 'integer' || p.type === 'decimal' || p.type === 'fraction' || p.type === 'group') {
      if (!replaced) {
        out += numberStr;
        replaced = true;
      }
    } else {
      out += p.value;
    }
  }
  return out;
}

/** Birim fiyat (ör. 100 ml başına). quantity ve per aynı birimdedir. */
export function unitPrice(
  price: Money,
  quantity: number,
  per: number,
  mode: RoundingMode = 'half_up',
): Money {
  if (quantity <= 0 || per <= 0) throw new MoneyError('Miktar pozitif olmalı');
  assertSafe(quantity, 'miktar');
  assertSafe(per, 'birim');
  const v = divRound(BigInt(price.amountMinor) * BigInt(per), BigInt(quantity), mode);
  return money(Number(v), price.currency);
}

/**
 * Fiyat sonu yuvarlama (perakende alışkanlığı). Tutar YUKARI doğru ilk uygun sona yuvarlanır;
 * böylece toplu zam/indirimde fiyat hiçbir zaman hesaplanandan düşük çıkmaz.
 *   whole → 120,00 · end_90 → 119,90 · end_99 → 119,99 (2 haneli para birimlerinde)
 */
export type PriceEnding = 'none' | 'whole' | 'end_90' | 'end_99';

export function roundPriceEnding(a: Money, ending: PriceEnding): Money {
  if (ending === 'none') return a;
  const unit = minorUnitOf(a.currency);
  if (unit !== 2) return a;
  const v = a.amountMinor;
  if (v <= 0) return a;
  const base = Math.floor(v / 100) * 100;
  const target = ending === 'whole' ? 0 : ending === 'end_90' ? 90 : 99;
  if (ending === 'whole') return money(v % 100 === 0 ? v : base + 100, a.currency);
  const candidate = base + target;
  return money(candidate >= v ? candidate : candidate + 100, a.currency);
}

/**
 * Sağlayıcı API'lerinden gelen ondalık metni ("5.20000000", "15.34", "0E-8", "1.2E+2") en küçük birime çevirir.
 * Ayırıcı her zaman noktadır (makine biçimi; binlik ayırıcı kabul edilmez). Kayan nokta kullanılmaz.
 * Birimden fazla ondalık varsa (ör. komisyon "0.125") `mode` ile yuvarlanır.
 */
export function decimalToMinor(input: string, currency: string, mode: RoundingMode = 'half_up'): number {
  const unit = minorUnitOf(currency);
  const m = /^\s*([+-]?)(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d+))?\s*$/.exec(input);
  if (!m) throw new MoneyError(`Geçersiz ondalık tutar: ${input}`);
  const [, sign, intPart, frac = '', expStr] = m;
  const digits = BigInt(intPart! + frac);
  // değer = digits × 10^(exp − frac.length); en küçük birim = değer × 10^unit
  const shift = Number(expStr ?? 0) - frac.length + unit;
  let minor: bigint;
  if (shift >= 0) minor = digits * 10n ** BigInt(shift);
  else minor = divRound(digits, 10n ** BigInt(-shift), mode);
  const n = Number(sign === '-' ? -minor : minor);
  assertSafe(n);
  return n;
}

/** En küçük birimden makine biçimli ondalık metne: 1234 → "12.34", 1200 → "12.00", JPY 500 → "500". */
export function minorToDecimal(amountMinor: number, currency: string): string {
  assertSafe(amountMinor);
  const unit = minorUnitOf(currency);
  const negative = amountMinor < 0;
  const abs = BigInt(Math.abs(amountMinor));
  const base = 10n ** BigInt(unit);
  const s = unit > 0 ? `${abs / base}.${(abs % base).toString().padStart(unit, '0')}` : `${abs}`;
  return negative ? `-${s}` : s;
}

/**
 * Google Ads mikro birimi (1 para birimi = 1.000.000 mikro) → en küçük birim (F7-33). TRY'de 1 kuruş = 10.000
 * mikro. Google int64 değerleri JSON'da metin gönderir; metin, bigint veya güvenli tam sayı kabul edilir. Kuruşa
 * tam bölünmeyen mikro (ör. harcama) `mode` ile yuvarlanır. Kayan nokta kullanılmaz.
 */
export function microsToMinor(
  micros: string | bigint | number,
  currency: string,
  mode: RoundingMode = 'half_up',
): number {
  const unit = minorUnitOf(currency);
  let m: bigint;
  if (typeof micros === 'bigint') m = micros;
  else if (typeof micros === 'number') {
    assertSafe(micros, 'mikro');
    m = BigInt(micros);
  } else {
    if (!/^\s*-?\d+\s*$/.test(micros)) throw new MoneyError(`Geçersiz mikro tutar: ${micros}`);
    m = BigInt(micros.trim());
  }
  const n = Number(divRound(m, 10n ** BigInt(6 - unit), mode));
  assertSafe(n);
  return n;
}

/** En küçük birim → Google Ads mikro birimi (metin; int64 JSON'da metindir). TRY: 1 kuruş = 10.000 mikro. */
export function minorToMicros(amountMinor: number, currency: string): string {
  assertSafe(amountMinor);
  const unit = minorUnitOf(currency);
  return (BigInt(amountMinor) * 10n ** BigInt(6 - unit)).toString();
}

/**
 * Hedef brüt marja göre KDV hariç satış fiyatı (F5B-11): net = maliyet / (1 − marj). Marj, KDV hariç satış fiyatı
 * üzerinden hesaplanır (perakendedeki "brüt kâr marjı"). Yukarı yuvarlanır: sonuç marjı hedefin altına düşmez.
 */
export function netPriceForMargin(cost: Money, marginBps: number): Money {
  if (!Number.isInteger(marginBps) || marginBps < 0 || marginBps >= 10_000)
    throw new MoneyError('Marj %0 ile %99,99 arasında olmalı');
  const v = divRound(BigInt(cost.amountMinor) * 10_000n, BigInt(10_000 - marginBps), 'up');
  return money(Number(v), cost.currency);
}

/**
 * Brüt marj (baz puan): (net − maliyet) / net. Net fiyat sıfır veya negatifse null. Sıfıra doğru yuvarlanır
 * (marj iyimser gösterilmez).
 */
export function grossMarginBps(net: Money, cost: Money): number | null {
  sameCurrency(net, cost);
  if (net.amountMinor <= 0) return null;
  const v = divRound(BigInt(net.amountMinor - cost.amountMinor) * 10_000n, BigInt(net.amountMinor), 'down');
  return Number(v);
}

/**
 * Kur dönüşümü (F5B-11): tutar × kur. Kur ondalık metindir ("34.1250", "0,0291"; en fazla 8 ondalık) ve kaynak
 * birimin 1'inin hedef birimdeki karşılığıdır. Hedef para biriminin birimine yuvarlanır; kayan nokta yok.
 */
export function convert(a: Money, rate: string, to: string, mode: RoundingMode = 'half_up'): Money {
  const m = /^\s*(\d+)(?:[.,](\d{1,8}))?\s*$/.exec(rate);
  if (!m) throw new MoneyError(`Geçersiz kur: ${rate}`);
  const frac = m[2] ?? '';
  const scaled = BigInt(`${m[1]}${frac}`);
  if (scaled === 0n) throw new MoneyError('Kur sıfır olamaz');
  // hedef_birim = kaynak_birim × kur × 10^(hedefBirim − kaynakBirim)
  let num = BigInt(a.amountMinor) * scaled;
  let den = 10n ** BigInt(frac.length);
  const shift = minorUnitOf(to) - minorUnitOf(a.currency);
  if (shift >= 0) num *= 10n ** BigInt(shift);
  else den *= 10n ** BigInt(-shift);
  return money(Number(divRound(num, den, mode)), to);
}

/** Ortak birimdeki (TL) karşılık: `unit` kadar biriminin değeri `rate` (TCMB biçimi; TL için rate "1", unit 1). */
export interface CrossRate {
  rate: string;
  unit: number;
}

function parseRate(rate: string): { n: bigint; scale: number } {
  const m = /^\s*(\d+)(?:\.(\d{1,8}))?\s*$/.exec(rate);
  if (!m) throw new MoneyError(`Geçersiz kur: ${rate}`);
  const frac = m[2] ?? '';
  const n = BigInt(`${m[1]}${frac}`);
  if (n === 0n) throw new MoneyError('Kur sıfır olamaz');
  return { n, scale: frac.length };
}

/**
 * Çapraz kur dönüşümü (F7-11): iki para biriminin TL karşılıklarından (TCMB değeri ve birimi) hedef tutar.
 * hedef = tutar × (kaynakKur / kaynakBirim) ÷ (hedefKur / hedefBirim) × (1 + bps/10000).
 * Tüm çarpımlar tam sayıyla yapılır, tek bölmede yuvarlanır (kayan nokta ve ara yuvarlama yok).
 */
export function convertCross(
  a: Money,
  from: CrossRate,
  to: CrossRate & { currency: string },
  markupBps = 0,
  mode: RoundingMode = 'half_up',
): Money {
  if (!Number.isSafeInteger(from.unit) || from.unit <= 0 || !Number.isSafeInteger(to.unit) || to.unit <= 0)
    throw new MoneyError('Kur birimi pozitif tam sayı olmalı');
  if (!Number.isInteger(markupBps) || markupBps <= -10_000) throw new MoneyError('Geçersiz oran');
  const f = parseRate(from.rate);
  const t = parseRate(to.rate);
  let num =
    BigInt(a.amountMinor) * f.n * 10n ** BigInt(t.scale) * BigInt(to.unit) * BigInt(10_000 + markupBps);
  let den = 10n ** BigInt(f.scale) * BigInt(from.unit) * t.n * 10_000n;
  const shift = minorUnitOf(to.currency) - minorUnitOf(a.currency);
  if (shift >= 0) num *= 10n ** BigInt(shift);
  else den *= 10n ** BigInt(-shift);
  return money(Number(divRound(num, den, mode)), to.currency);
}
